import {
  parseISO,
  isBefore,
  subYears,
  addMonths,
  subMonths,
  format as formatDate,
} from "date-fns";
import { enGB, sv, fi } from "date-fns/locale";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { FieldRequestFieldEnum } from "@krea/customer-features/types/fieldRequest";
import { Alert } from "@krea/shared/design-system/alerts/Alert";

interface ITriageAlertProps {
  field: FieldRequestFieldEnum;
}

const SelfEstimatedHistoricRevenueAlert = () => {
  const { t, i18n } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();

  const incorporationDate =
    data?.applyingCompany.customerCompany.incorporationDate;

  const now = useMemo(() => new Date(), []);

  const showTextForOldCompanies = useMemo(() => {
    if (!incorporationDate) {
      return false;
    }
    // Parse the date string into a Date object
    const inputDate = parseISO(incorporationDate);
    // Get the date for exactly 1 year ago from today
    const oneYearAgo = subYears(now, 1);

    // Check if the input date is before the one year ago date
    return isBefore(inputDate, oneYearAgo);
  }, [incorporationDate, now]);

  const DATE_FORMAT = "MMMM yyyy";

  // Adjust start and end of the year based on the current date
  const endDate = subMonths(now, 1); // 1 month earlier
  const startDate = subMonths(endDate, 11); // 11 months earlier

  // Format dates based on language
  const localeMap = { en: enGB, sv, fi };
  const currentLanguage = i18n.language;
  const locale =
    currentLanguage in localeMap
      ? localeMap[currentLanguage as keyof typeof localeMap]
      : enGB;

  const formattedStartDate = formatDate(startDate, DATE_FORMAT, { locale });
  const formattedEndDate = formatDate(endDate, DATE_FORMAT, { locale });

  if (showTextForOldCompanies) {
    return (
      <Alert variant="warning" isClosable>
        {`${t("iaq.selfEstimatedHistoricRevenue.alertText")} ${formattedStartDate} - ${formattedEndDate}.`}
      </Alert>
    );
  }

  return null;
};

export const TriageAlert = ({ field }: ITriageAlertProps) => {
  if (field === FieldRequestFieldEnum.self_estimated_historic_revenue) {
    return <SelfEstimatedHistoricRevenueAlert />;
  }

  return null;
};
